// src/api/auth.js
// Authentication + credential recovery — API docs §1 and §2.
//
// Every endpoint answers with the standard envelope
// `{ success, statusCode, message, data, errors }`. These helpers return that
// envelope as-is (callers read `res.data`), with one deliberate exception:
// `getMe` unwraps to the Partner object itself, because the whole app treats
// the profile as a plain partner record (`partner.kind`, `partner.name`, …).
import api from "../lib/apiClient";

// §1.1 — `cf_turnstile_token` is this panel's human-verification field; the
// backend ignores it when Turnstile is disabled.
export const login = (username, password, cf_turnstile_token) =>
  api
    .post("/auth/login", { username, password, cf_turnstile_token })
    .then((r) => r.data);

// §1.2 — refresh tokens rotate: the response carries a *new* refresh token and
// invalidates the old one. The response interceptor in lib/apiClient.js drives
// this automatically on a 401; this export exists for explicit refreshes.
export const refresh = (refreshToken) =>
  api.post("/auth/refresh", { refreshToken }).then((r) => r.data);

// §1.3 — revokes the session for this device only.
export const logout = (refreshToken) =>
  api.post("/auth/logout", { refreshToken }).then((r) => r.data);

// §1.4 — the Partner object (§6.1). Read `kind` ("CHAIN" | "BRANCH") off it to
// decide which panel to render.
export const getMe = () =>
  api.get("/auth/me").then((r) => r.data?.data ?? r.data);

// §2.1 / §2.2 — forgot username: OTP to the registered phone, then the OTP is
// exchanged for the username itself.
export const forgotUsernameRequestOtp = (phone) =>
  api.post("/auth/forgot-username/request-otp", { phone }).then((r) => r.data);

export const forgotUsernameVerifyOtp = (phone, otp) =>
  api.post("/auth/forgot-username/verify-otp", { phone, otp }).then((r) => r.data);

// §2.3 / §2.4 — forgot password: OTP to the phone on record, then the OTP plus
// a new password. On success *all* sessions are revoked and the partner must
// log in again.
export const forgotPasswordRequestOtp = (username) =>
  api.post("/auth/forgot-password/request-otp", { username }).then((r) => r.data);

export const forgotPasswordVerifyOtp = (username, otp, newPassword) =>
  api
    .post("/auth/forgot-password/verify-otp", { username, otp, newPassword })
    .then((r) => r.data);
